import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dna,
  Shield,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Clock,
  Plus,
  FileText,
  Upload,
  Share2,
  Eye,
  EyeOff,
  Users,
  Lock,
  Globe,
  UserCheck,
  ArrowLeft,
  Loader2,
  MoreVertical,
  Trash2,
  Edit,
  ExternalLink,
  Download,
  Copy,
  Search,
  Filter,
  ChevronRight,
  Activity,
  Heart,
  Microscope,
  FlaskConical
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resultColors = {
  clear: 'bg-green-100 text-green-700 border-green-200',
  carrier: 'bg-amber-100 text-amber-700 border-amber-200',
  at_risk: 'bg-red-100 text-red-700 border-red-200',
  not_tested: 'bg-gray-100 text-gray-500 border-gray-200',
  pending: 'bg-blue-100 text-blue-600 border-blue-200'
};

const resultIcons = {
  clear: CheckCircle,
  carrier: AlertTriangle,
  at_risk: AlertCircle,
  not_tested: Clock,
  pending: Clock
};

const privacyIcons = {
  public: Globe,
  members: Users,
  friends: UserCheck,
  private: Lock
};

const GeneticHealthChecklist = () => {
  const { petId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [pet, setPet] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [tests, setTests] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [providers, setProviders] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [privacySettings, setPrivacySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [addTestDialogOpen, setAddTestDialogOpen] = useState(false);
  const [addDocDialogOpen, setAddDocDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [testForm, setTestForm] = useState({
    test_provider: '',
    test_date: new Date().toISOString().split('T')[0],
    test_type: 'comprehensive',
    results: {},
    notes: '',
    privacy_level: 'private',
    share_anonymously_for_research: false
  });
  
  const [docForm, setDocForm] = useState({
    document_type: 'dna_test',
    title: '',
    description: '',
    file_url: '',
    file_name: '',
    document_date: new Date().toISOString().split('T')[0],
    privacy_level: 'private',
    is_sensitive: false,
    tags: []
  });
  
  const [shareForm, setShareForm] = useState({
    shared_with_email: '',
    shared_with_name: '',
    message: '',
    expires_days: 30,
    allow_download: true,
    include_sensitive: false
  });

  useEffect(() => {
    if (user && petId) {
      fetchData();
    }
  }, [user, petId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [petRes, checklistRes, testsRes, docsRes, providersRes, conditionsRes, privacyRes] = await Promise.all([
        axios.get(`${API}/pets/${petId}`),
        axios.get(`${API}/genetic-health/checklist/${petId}`),
        axios.get(`${API}/genetic-health/tests/pet/${petId}`),
        axios.get(`${API}/genetic-health/documents/pet/${petId}`),
        axios.get(`${API}/genetic-health/providers`),
        axios.get(`${API}/genetic-health/conditions/flat`),
        axios.get(`${API}/genetic-health/privacy/${petId}`).catch(() => ({ data: null }))
      ]);
      
      setPet(petRes.data);
      setChecklist(checklistRes.data);
      setTests(testsRes.data);
      setDocuments(docsRes.data);
      setProviders(providersRes.data);
      setConditions(conditionsRes.data);
      setPrivacySettings(privacyRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load health data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTest = async () => {
    if (!testForm.test_provider) {
      toast.error('Please select a test provider');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/genetic-health/tests`, {
        pet_id: petId,
        ...testForm
      });
      toast.success('Test results recorded!');
      setAddTestDialogOpen(false);
      setTestForm({
        test_provider: '',
        test_date: new Date().toISOString().split('T')[0],
        test_type: 'comprehensive',
        results: {},
        notes: '',
        privacy_level: 'private',
        share_anonymously_for_research: false
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save test');
    } finally {
      setSaving(false);
    }
  };

  const handleAddDocument = async () => {
    if (!docForm.title || !docForm.file_url) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/genetic-health/documents`, {
        pet_id: petId,
        ...docForm
      });
      toast.success('Document uploaded!');
      setAddDocDialogOpen(false);
      setDocForm({
        document_type: 'dna_test',
        title: '',
        description: '',
        file_url: '',
        file_name: '',
        document_date: new Date().toISOString().split('T')[0],
        privacy_level: 'private',
        is_sensitive: false,
        tags: []
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    if (!shareForm.shared_with_email) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/genetic-health/documents/share`, {
        pet_id: petId,
        ...shareForm
      });
      toast.success('Share link created!');
      
      // Copy link to clipboard
      const fullLink = `${window.location.origin}${response.data.share_link}`;
      navigator.clipboard.writeText(fullLink);
      toast.info('Link copied to clipboard!');
      
      setShareDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create share link');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePrivacy = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/genetic-health/privacy/${petId}`, {
        pet_id: petId,
        ...privacySettings
      });
      toast.success('Privacy settings updated!');
      setPrivacyDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm('Delete this test record?')) return;
    
    try {
      await axios.delete(`${API}/genetic-health/tests/${testId}`);
      toast.success('Test deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete test');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    
    try {
      await axios.delete(`${API}/genetic-health/documents/${docId}`);
      toast.success('Document deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const ResultBadge = ({ result }) => {
    const Icon = resultIcons[result] || Clock;
    const colorClass = resultColors[result] || resultColors.not_tested;
    const labels = {
      clear: 'Clear',
      carrier: 'Carrier',
      at_risk: 'At Risk',
      not_tested: 'Not Tested',
      pending: 'Pending'
    };
    
    return (
      <Badge className={`${colorClass} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {labels[result] || result}
      </Badge>
    );
  };

  const PrivacyBadge = ({ level }) => {
    const Icon = privacyIcons[level] || Lock;
    const labels = {
      public: 'Public',
      members: 'Members',
      friends: 'Friends',
      private: 'Private'
    };
    
    return (
      <Badge variant="outline" className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {labels[level] || level}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!pet) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="text-center py-16">
          <CardContent>
            <AlertCircle className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-heading text-xl mb-2">Pet not found</h3>
            <Button onClick={() => navigate('/my-pets')}>Go to My Pets</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-8">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            className="text-white/80 hover:text-white mb-4"
            onClick={() => navigate('/health')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Health
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-heading font-medium flex items-center gap-3">
                <Dna className="w-8 h-8" />
                {pet.name}'s Genetic Health
              </h1>
              <p className="text-white/80 mt-1">
                {pet.breed || 'Mixed breed'} • Track genetic tests and health documents
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="border-white/30 text-white hover:bg-white/20"
                onClick={() => setPrivacyDialogOpen(true)}
              >
                <Shield className="w-4 h-4 mr-2" />
                Privacy
              </Button>
              <Button
                variant="outline"
                className="border-white/30 text-white hover:bg-white/20"
                onClick={() => setShareDialogOpen(true)}
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Summary Cards */}
        {checklist && (
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Checklist Progress</p>
                    <p className="text-2xl font-bold">{checklist.summary.completion_percentage}%</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Activity className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <Progress value={checklist.summary.completion_percentage} className="mt-2" />
                <p className="text-xs text-muted-foreground mt-1">
                  {checklist.summary.tested_recommended}/{checklist.summary.total_recommended} recommended tests
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Clear Results</p>
                    <p className="text-2xl font-bold text-green-600">{checklist.summary.clear}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Carrier Status</p>
                    <p className="text-2xl font-bold text-amber-600">{checklist.summary.carrier}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">At Risk</p>
                    <p className="text-2xl font-bold text-red-600">{checklist.summary.at_risk}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content */}
        <Tabs defaultValue="checklist" className="w-full">
          <TabsList className="w-full justify-start mb-6">
            <TabsTrigger value="checklist">Health Checklist</TabsTrigger>
            <TabsTrigger value="tests">Test Records ({tests.length})</TabsTrigger>
            <TabsTrigger value="documents">Documents ({documents.length})</TabsTrigger>
            <TabsTrigger value="compare">Compare</TabsTrigger>
          </TabsList>

          {/* Checklist Tab */}
          <TabsContent value="checklist">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-xl">Recommended Tests for {pet.breed || 'Your Dog'}</h2>
              <Button onClick={() => setAddTestDialogOpen(true)} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Record Test Results
              </Button>
            </div>
            
            {checklist && checklist.checklist.length > 0 ? (
              <div className="space-y-3">
                {checklist.checklist.map((item) => (
                  <Card key={item.id} className={item.tested ? 'border-l-4 border-l-green-500' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            item.result === 'clear' ? 'bg-green-100' :
                            item.result === 'carrier' ? 'bg-amber-100' :
                            item.result === 'at_risk' ? 'bg-red-100' :
                            'bg-gray-100'
                          }`}>
                            {item.tested ? (
                              React.createElement(resultIcons[item.result] || CheckCircle, {
                                className: `w-5 h-5 ${
                                  item.result === 'clear' ? 'text-green-600' :
                                  item.result === 'carrier' ? 'text-amber-600' :
                                  item.result === 'at_risk' ? 'text-red-600' :
                                  'text-gray-400'
                                }`
                              })
                            ) : (
                              <Dna className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">{item.name}</h3>
                              {item.recommended && (
                                <Badge variant="outline" className={`text-xs ${
                                  item.priority === 'high' ? 'border-red-300 text-red-600' : 'border-amber-300 text-amber-600'
                                }`}>
                                  {item.priority === 'high' ? 'High Priority' : 'Recommended'}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{item.description}</p>
                            {item.test_date && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Tested: {new Date(item.test_date).toLocaleDateString()} • {item.provider}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <ResultBadge result={item.result || 'not_tested'} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="text-center py-12">
                <CardContent>
                  <Dna className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-heading text-xl mb-2">No tests recorded yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Start tracking your pet's genetic health by recording test results.
                  </p>
                  <Button onClick={() => setAddTestDialogOpen(true)} className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Record First Test
                  </Button>
                </CardContent>
              </Card>
            )}
            
            {/* Action Items */}
            {checklist && checklist.recommendations.action_items.length > 0 && (
              <div className="mt-8">
                <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Action Items
                </h3>
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-4">
                    <ul className="space-y-2">
                      {checklist.recommendations.action_items.map((item) => (
                        <li key={item.id} className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                          <div>
                            <span className="font-medium">{item.name}</span>
                            <span className="text-muted-foreground"> - {item.result === 'carrier' ? 'Carrier status' : 'At risk'}</span>
                            <p className="text-sm text-muted-foreground">Discuss with your veterinarian.</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Tests Tab */}
          <TabsContent value="tests">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-xl">Test Records</h2>
              <Button onClick={() => setAddTestDialogOpen(true)} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Add Test
              </Button>
            </div>
            
            {tests.length > 0 ? (
              <div className="space-y-4">
                {tests.map((test) => (
                  <Card key={test.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                            <FlaskConical className="w-6 h-6 text-purple-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">{test.test_provider_name}</h3>
                              <PrivacyBadge level={test.privacy_level} />
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {new Date(test.test_date).toLocaleDateString()} • {test.test_type}
                            </p>
                            {test.notes && (
                              <p className="text-sm text-muted-foreground mt-1">{test.notes}</p>
                            )}
                            {Object.keys(test.results).length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {Object.entries(test.results).slice(0, 5).map(([cond, result]) => (
                                  <ResultBadge key={cond} result={result} />
                                ))}
                                {Object.keys(test.results).length > 5 && (
                                  <Badge variant="outline">+{Object.keys(test.results).length - 5} more</Badge>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => handleDeleteTest(test.id)}>
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="text-center py-12">
                <CardContent>
                  <FlaskConical className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-heading text-xl mb-2">No test records</h3>
                  <p className="text-muted-foreground mb-4">
                    Record your pet's DNA test results to track their genetic health.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-xl">Health Documents</h2>
              <Button onClick={() => setAddDocDialogOpen(true)} className="btn-primary">
                <Upload className="w-4 h-4 mr-2" />
                Upload Document
              </Button>
            </div>
            
            {documents.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.map((doc) => (
                  <Card key={doc.id} className="card-interactive">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-sm">{doc.title}</h3>
                              {doc.is_sensitive && (
                                <Lock className="w-3 h-3 text-red-500" />
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {doc.document_type.replace('_', ' ')}
                            </p>
                            {doc.document_date && (
                              <p className="text-xs text-muted-foreground">
                                {new Date(doc.document_date).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => window.open(doc.file_url, '_blank')}>
                              <Download className="w-4 h-4 mr-2" />
                              Download
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDeleteDocument(doc.id)}>
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        <PrivacyBadge level={doc.privacy_level} />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="text-center py-12">
                <CardContent>
                  <FileText className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-heading text-xl mb-2">No documents</h3>
                  <p className="text-muted-foreground mb-4">
                    Upload health documents like DNA test reports, vet records, and more.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Compare Tab */}
          <TabsContent value="compare">
            <Card className="text-center py-12">
              <CardContent>
                <Microscope className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-heading text-xl mb-2">Compare Genetic Profiles</h3>
                <p className="text-muted-foreground mb-4">
                  Compare {pet.name}'s genetic test results with breed averages or other dogs.
                </p>
                <Button onClick={() => setCompareDialogOpen(true)} className="btn-primary">
                  <Search className="w-4 h-4 mr-2" />
                  Start Comparison
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Test Dialog */}
      <Dialog open={addTestDialogOpen} onOpenChange={setAddTestDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Record Test Results</DialogTitle>
            <DialogDescription>
              Add genetic test results from DNA testing services.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Test Provider *</label>
              <Select
                value={testForm.test_provider}
                onValueChange={(val) => setTestForm({ ...testForm, test_provider: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} {p.conditions_tested > 0 && `(${p.conditions_tested} conditions)`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Test Date</label>
              <Input
                type="date"
                value={testForm.test_date}
                onChange={(e) => setTestForm({ ...testForm, test_date: e.target.value })}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Results</label>
              <p className="text-xs text-muted-foreground mb-2">
                Select results for tested conditions:
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                {conditions.slice(0, 10).map((cond) => (
                  <div key={cond.id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                    <span className="text-sm">{cond.name}</span>
                    <Select
                      value={testForm.results[cond.id] || ''}
                      onValueChange={(val) => setTestForm({
                        ...testForm,
                        results: { ...testForm.results, [cond.id]: val }
                      })}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="clear">Clear</SelectItem>
                        <SelectItem value="carrier">Carrier</SelectItem>
                        <SelectItem value="at_risk">At Risk</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Notes</label>
              <Textarea
                placeholder="Any additional notes..."
                value={testForm.notes}
                onChange={(e) => setTestForm({ ...testForm, notes: e.target.value })}
                rows={2}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Privacy</label>
              <Select
                value={testForm.privacy_level}
                onValueChange={(val) => setTestForm({ ...testForm, privacy_level: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private - Only you</SelectItem>
                  <SelectItem value="friends">Friends Only</SelectItem>
                  <SelectItem value="members">All Members</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between">
              <label className="text-sm">Share anonymously for research</label>
              <Switch
                checked={testForm.share_anonymously_for_research}
                onCheckedChange={(val) => setTestForm({ ...testForm, share_anonymously_for_research: val })}
              />
            </div>
            
            <Button onClick={handleAddTest} className="w-full btn-primary" disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Test Results
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Document Dialog */}
      <Dialog open={addDocDialogOpen} onOpenChange={setAddDocDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Upload Health Document</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Document Type</label>
              <Select
                value={docForm.document_type}
                onValueChange={(val) => setDocForm({ ...docForm, document_type: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dna_test">DNA Test Report</SelectItem>
                  <SelectItem value="vet_record">Veterinary Record</SelectItem>
                  <SelectItem value="vaccination">Vaccination Record</SelectItem>
                  <SelectItem value="health_certificate">Health Certificate</SelectItem>
                  <SelectItem value="xray">X-Ray / Imaging</SelectItem>
                  <SelectItem value="lab_result">Lab Results</SelectItem>
                  <SelectItem value="prescription">Prescription</SelectItem>
                  <SelectItem value="insurance">Insurance Document</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Input
              placeholder="Document Title *"
              value={docForm.title}
              onChange={(e) => setDocForm({ ...docForm, title: e.target.value })}
            />
            
            <Input
              placeholder="File URL *"
              value={docForm.file_url}
              onChange={(e) => setDocForm({ ...docForm, file_url: e.target.value, file_name: e.target.value.split('/').pop() })}
            />
            
            <Input
              type="date"
              value={docForm.document_date}
              onChange={(e) => setDocForm({ ...docForm, document_date: e.target.value })}
            />
            
            <Textarea
              placeholder="Description (optional)"
              value={docForm.description}
              onChange={(e) => setDocForm({ ...docForm, description: e.target.value })}
              rows={2}
            />
            
            <div>
              <label className="text-sm font-medium mb-2 block">Privacy</label>
              <Select
                value={docForm.privacy_level}
                onValueChange={(val) => setDocForm({ ...docForm, privacy_level: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private</SelectItem>
                  <SelectItem value="friends">Friends Only</SelectItem>
                  <SelectItem value="members">All Members</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between">
              <label className="text-sm">Mark as sensitive</label>
              <Switch
                checked={docForm.is_sensitive}
                onCheckedChange={(val) => setDocForm({ ...docForm, is_sensitive: val })}
              />
            </div>
            
            <Button onClick={handleAddDocument} className="w-full btn-primary" disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Upload Document
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Share Health Records</DialogTitle>
            <DialogDescription>
              Create a secure link to share {pet.name}'s health records with veterinarians or family.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <Input
              placeholder="Recipient Email *"
              type="email"
              value={shareForm.shared_with_email}
              onChange={(e) => setShareForm({ ...shareForm, shared_with_email: e.target.value })}
            />
            
            <Input
              placeholder="Recipient Name (optional)"
              value={shareForm.shared_with_name}
              onChange={(e) => setShareForm({ ...shareForm, shared_with_name: e.target.value })}
            />
            
            <Textarea
              placeholder="Message (optional)"
              value={shareForm.message}
              onChange={(e) => setShareForm({ ...shareForm, message: e.target.value })}
              rows={2}
            />
            
            <div>
              <label className="text-sm font-medium mb-2 block">Link Expires In</label>
              <Select
                value={String(shareForm.expires_days)}
                onValueChange={(val) => setShareForm({ ...shareForm, expires_days: parseInt(val) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="365">1 year</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between">
              <label className="text-sm">Allow downloads</label>
              <Switch
                checked={shareForm.allow_download}
                onCheckedChange={(val) => setShareForm({ ...shareForm, allow_download: val })}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <label className="text-sm">Include sensitive documents</label>
              <Switch
                checked={shareForm.include_sensitive}
                onCheckedChange={(val) => setShareForm({ ...shareForm, include_sensitive: val })}
              />
            </div>
            
            <Button onClick={handleShare} className="w-full btn-primary" disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Create Share Link
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Privacy Settings Dialog */}
      <Dialog open={privacyDialogOpen} onOpenChange={setPrivacyDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Health Privacy Settings
            </DialogTitle>
          </DialogHeader>
          
          {privacySettings && (
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Default Test Privacy</label>
                <Select
                  value={privacySettings.default_test_privacy}
                  onValueChange={(val) => setPrivacySettings({ ...privacySettings, default_test_privacy: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">Private</SelectItem>
                    <SelectItem value="friends">Friends Only</SelectItem>
                    <SelectItem value="members">All Members</SelectItem>
                    <SelectItem value="public">Public</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Default Document Privacy</label>
                <Select
                  value={privacySettings.default_document_privacy}
                  onValueChange={(val) => setPrivacySettings({ ...privacySettings, default_document_privacy: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">Private</SelectItem>
                    <SelectItem value="friends">Friends Only</SelectItem>
                    <SelectItem value="members">All Members</SelectItem>
                    <SelectItem value="public">Public</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-3 pt-4 border-t">
                <h4 className="font-medium text-sm">Research & Comparison</h4>
                
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm">Allow anonymous research</label>
                    <p className="text-xs text-muted-foreground">Share data anonymously for breed research</p>
                  </div>
                  <Switch
                    checked={privacySettings.allow_anonymous_research}
                    onCheckedChange={(val) => setPrivacySettings({ ...privacySettings, allow_anonymous_research: val })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm">Include in breed statistics</label>
                    <p className="text-xs text-muted-foreground">Help improve breed health data</p>
                  </div>
                  <Switch
                    checked={privacySettings.allow_breed_statistics}
                    onCheckedChange={(val) => setPrivacySettings({ ...privacySettings, allow_breed_statistics: val })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm">Allow comparison requests</label>
                    <p className="text-xs text-muted-foreground">Others can compare with your pet's results</p>
                  </div>
                  <Switch
                    checked={privacySettings.allow_comparison_requests}
                    onCheckedChange={(val) => setPrivacySettings({ ...privacySettings, allow_comparison_requests: val })}
                  />
                </div>
              </div>
              
              <div className="space-y-3 pt-4 border-t">
                <h4 className="font-medium text-sm">Contact Preferences</h4>
                
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm">Allow breeder contact</label>
                    <p className="text-xs text-muted-foreground">Breeders can contact you about your pet</p>
                  </div>
                  <Switch
                    checked={privacySettings.allow_breeder_contact}
                    onCheckedChange={(val) => setPrivacySettings({ ...privacySettings, allow_breeder_contact: val })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm">Allow researcher contact</label>
                    <p className="text-xs text-muted-foreground">Researchers can contact you about studies</p>
                  </div>
                  <Switch
                    checked={privacySettings.allow_researcher_contact}
                    onCheckedChange={(val) => setPrivacySettings({ ...privacySettings, allow_researcher_contact: val })}
                  />
                </div>
              </div>
              
              <Button onClick={handleUpdatePrivacy} className="w-full btn-primary" disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Save Privacy Settings
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Compare Dialog */}
      <Dialog open={compareDialogOpen} onOpenChange={setCompareDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Compare Genetic Profiles</DialogTitle>
            <DialogDescription>
              Compare {pet.name}'s results with breed averages or other dogs.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <Card className="bg-muted/50">
              <CardContent className="p-4">
                <h4 className="font-medium mb-2">Compare with Breed Average</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  See how {pet.name}'s results compare to other {pet.breed || 'dogs'} in our database.
                </p>
                <Button className="w-full">
                  Compare with {pet.breed || 'Breed'} Average
                </Button>
              </CardContent>
            </Card>
            
            <Card className="bg-muted/50">
              <CardContent className="p-4">
                <h4 className="font-medium mb-2">Compare with Specific Dog</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Compare genetic profiles with another dog (requires their permission).
                </p>
                <Input placeholder="Enter dog's profile ID or search..." className="mb-2" />
                <Button variant="outline" className="w-full">
                  Search Dogs
                </Button>
              </CardContent>
            </Card>
            
            <p className="text-xs text-muted-foreground text-center">
              Only dogs whose owners allow comparisons will appear in search results.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GeneticHealthChecklist;
